import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

export type ScrollEdges = {
  /** There is more content above the fold. */
  top: boolean;
  /** There is more content below the fold. */
  bottom: boolean;
};

/**
 * Tracks whether a vertical scroll container has content above or below the
 * fold, so a `<ScrollEdgeFade />` can mark that edge — otherwise a clipped list
 * looks complete and the rows past the fold go unnoticed.
 *
 * Re-measures on scroll (wire `syncScrollEdges` to `onScroll`), on layout
 * changes (`ResizeObserver`) and when rows are added or removed
 * (`MutationObserver`), so callers never have to thread a dependency list in.
 */
export function useScrollEdges<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState<ScrollEdges>({
    top: false,
    bottom: false,
  });

  const syncScrollEdges = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    // Return the previous object when nothing changed so the state update is a
    // no-op — the ResizeObserver would otherwise re-render on every layout tick.
    setEdges(prev =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Measure after layout/paint so scrollHeight vs clientHeight is accurate.
    const raf = requestAnimationFrame(syncScrollEdges);
    const resizeObserver = new ResizeObserver(syncScrollEdges);
    resizeObserver.observe(el);
    const mutationObserver = new MutationObserver(syncScrollEdges);
    mutationObserver.observe(el, { childList: true });
    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [syncScrollEdges]);

  return { ref, edges, syncScrollEdges };
}

/**
 * Soft fade at the top/bottom edge of a scroll region, shown only when there is
 * more content in that direction. Absolutely positioned — put it in a
 * `position: relative` wrapper alongside (not inside) the scrolling element, so
 * it stays pinned to the fold instead of scrolling away. Non-interactive, so
 * the rows underneath stay clickable.
 */
export const ScrollEdgeFade = styled.div<{
  $side: 'top' | 'bottom';
  $visible: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 120ms ease;
  ${props =>
    props.$side === 'top'
      ? css`
          top: 0;
          height: 18px;
          background: linear-gradient(
            to bottom,
            var(--background),
            transparent
          );
        `
      : css`
          bottom: 0;
          /* Taller, with a solid hold near the edge, so it visibly dims the
             partial row at the fold on the dark theme — a short
             background-to-transparent gradient reads as nothing here. */
          height: 36px;
          background: linear-gradient(
            to top,
            var(--background) 0%,
            var(--background) 25%,
            transparent 100%
          );
        `}
`;
